// TrainingWebsites.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Pagination,
  Form,
  Dropdown,
  DropdownButton,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { PrimaryButton, DangerButton, CancelButton } from '../../Components/Buttons';
import OutlinedPrimaryButton from '../../Components/Buttons/OutlinedPrimaryButton';
import { useWorkspace } from '../../Context/WorkspaceContext';
import {
  BsArrowRepeat,
  BsChevronDown,
  BsChevronRight,
  BsLink,
  BsGlobe,
  BsCalendarEvent,
  BsCheckCircle,
  BsClockHistory,
  BsCheck2Square,
  BsSquare
} from 'react-icons/bs'; // Import additional icons
import CustomTooltip from '../../Components/CustomTooltip';
import CompactUpgradePrompt from '../../Components/CompactUpgradePrompt';
import { marked } from 'marked';
import { getAppDisplayName, getAppColor } from './bvUtils';
import { useLanguage } from '../../Context/LanguageContext';
import './agent-training.css';

const LINKS_PAGE_SIZE = 10;
const PAGE_SIZE = 10;

const TrainingWebsites = ({
  data,
  currentPage,
  onPageChange,
  editMode, // boolean: true when in website edit mode
  selectedItems,
  onItemSelect,
  websitePage,
  onWebsitePageChange,
  websiteLinkSearch,
  onWebsiteLinkSearch,
  expandedWebsiteKey,
  onWebsiteToggle,
  onWebsiteSelect, // Callback to toggle selection of all links of a website
  isAllWebsitesSelected, // boolean: true when all websites are selected
  selectAllLoading, // object: keys are domain names or '__all__', values are loading states
  workspaceId, // workspace id passed from the parent
  apiUrl, // API URL from the parent/global context
  onPollState, // callback to poll the system state
  onFetchTrainedData,
  systemStatus, // system status passed from the parent
  crawledUrlCount, // URL count for checking limits
  crawlUrlLimit, // URL limit for disabling features
  userData, // Add userData to props
  onAssignContents, // Assignment control callback
}) => {
  const { t } = useLanguage();
  // Training features are available to all workspace members
  const isAdmin = true;

  // Get workspace data for website sync permissions
  const { workspaceData } = useWorkspace();
  const workspaceWebsiteSync = workspaceData?.workspace_website_sync_options || [];

  // Check if URL crawl limit has been reached
  const isUrlLimitReached = crawlUrlLimit && crawledUrlCount >= crawlUrlLimit;

  // Add state for tracking loading status for each website
  const [loadingStates, setLoadingStates] = useState({});

  // Add state for confirmation modals
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null); // Can hold websiteItem, linkValue, scheduleType, or errorMessage

  // Add state for content viewing modal
  const [showContentModal, setShowContentModal] = useState(false);
  const [contentData, setContentData] = useState({ title: '', content: '', type: '', created_at: '' });
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentPage, setContentPage] = useState(1);
  const [contentPages, setContentPages] = useState([]);
  const CONTENT_CHARS_PER_PAGE = 5000; // Characters per page

  // Add state for tracking window width
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Add state for chatbots and apps
  const [chatbots, setChatbots] = useState([]);
  const [apps, setApps] = useState([]);

  // NEW: State for website links (fetched when website is expanded)
  const [websiteLinks, setWebsiteLinks] = useState({}); // { domainName: { links: [], totalCount: 0, totalPages: 1, currentPage: 1 } }
  const [loadingLinks, setLoadingLinks] = useState({}); // { domainName: boolean }

  // Add useEffect to track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch chatbots when component mounts
  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/chatbot/get-chatbots`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ workspace_id: workspaceId })
          }
        );
        if (response.ok) {
          const data = await response.json();
          setChatbots(data.chatbots || []);
        }
      } catch (error) {
      }
    };

    if (workspaceId) {
      fetchChatbots();
    }
  }, [workspaceId, apiUrl]);

  // Fetch apps when component mounts
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/apps/get-apps`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setApps(data.apps || []);
        }
      } catch (error) {
      }
    };

    if (apiUrl) {
      fetchApps();
    }
  }, [apiUrl]);

  // Debounced search effect - triggers API call when search changes
  useEffect(() => {
    // Only run if there's an expanded website
    if (!expandedWebsiteKey) return;

    const searchQuery = websiteLinkSearch[expandedWebsiteKey];

    // If there's a search query, debounce it
    // If no search query (initial load), fetch immediately
    const delay = searchQuery && searchQuery.trim() ? 500 : 0;

    const timeoutId = setTimeout(() => {
      // Fetch links with search query (reset to page 1 on new search)
      fetchWebsiteLinks(expandedWebsiteKey, 1, LINKS_PAGE_SIZE, searchQuery || null);
    }, delay);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteLinkSearch, expandedWebsiteKey]);

  // Function to break content into pages
  const getContentPages = (content) => {
    if (!content) return [];

    // If content is short enough, return as single page
    if (content.length <= CONTENT_CHARS_PER_PAGE) return [content];

    const pages = [];
    let i = 0;

    // Create pages of approximately equal length
    while (i < content.length) {
      pages.push(content.substring(i, i + CONTENT_CHARS_PER_PAGE));
      i += CONTENT_CHARS_PER_PAGE;
    }

    return pages;
  };

  // Modify the tooltip wrapper to handle both disabled state, regular tooltips, and plan restrictions
  const wrapWithTooltip = (child, tooltipContent) => {
    let tooltip;

    if (isUrlLimitReached) {
      tooltip = t('dashboard.training.trainingwebsites.tooltipUpgradeRequired');
    } else if (systemStatus !== 'OK') {
      tooltip = '';
    } else {
      tooltip = tooltipContent;
    }

    return (
      <CustomTooltip
        content={tooltip}
        placement="top"
      >
        {child}
      </CustomTooltip>
    );
  };

  // Helper function to check if a schedule type is allowed for the workspace
  const isScheduleAllowed = (scheduleType) => {
    if (!workspaceWebsiteSync || workspaceWebsiteSync.length === 0) return false;

    // Convert scheduleType to match the format in workspace sync options
    let scheduleKey;
    switch (scheduleType) {
      case 'daily': scheduleKey = 'DAILY'; break;
      case 'weekly': scheduleKey = 'WEEKLY'; break;
      case 'monthly': scheduleKey = 'MONTHLY'; break;
      case 'none': scheduleKey = 'NONE'; break;
      default: scheduleKey = scheduleType.toUpperCase();
    }

    return workspaceWebsiteSync.includes(scheduleKey);
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const itemsToShow = data.slice(startIndex, startIndex + PAGE_SIZE);

  // Function to show confirmation/error modal
  const showConfirm = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };

  // Function to fetch and display content
  const handleViewContent = async (contentTitle) => {
    if (editMode) return; // Don't fetch content in edit mode

    try {
      setIsContentLoading(true);
      setShowContentModal(true);
      setContentPage(1); // Reset to first page
      // Reset content data to avoid showing old title while loading new content
      setContentData({ title: '', content: '', type: '', created_at: '' });

      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(
        `${apiUrl}/api/v1/train/get-content-of-trained-data?workspace_id=${workspaceId}&content_title=${encodeURIComponent(contentTitle)}`,
        {
          headers: { Authorization: `Bearer ${brandvoxToken}` },
        }
      );

      if (!response.ok) {
        throw new Error(t('dashboard.training.trainingwebsites.errorFetchContentFailed'));
      }

      const data = await response.json();
      setContentData({
        title: data.content_title,
        content: data.content,
        type: data.type,
        created_at: data.created_at
      });
      setContentPages(getContentPages(data.content));
    } catch (err) {
      alert(err.message);
    } finally {
      setIsContentLoading(false);
    }
  };

  // Get confirmation/error message based on action
  const getConfirmMessage = () => {
    switch (confirmAction) {
      case 'syncWebsite':
        return t('dashboard.training.trainingwebsites.confirmSyncWebsite');
      case 'syncLinks':
        return t('dashboard.training.trainingwebsites.confirmSyncLinks');
      case 'syncLink':
        return t('dashboard.training.trainingwebsites.confirmSyncLink');
      case 'scheduleChange':
        return t('dashboard.training.trainingwebsites.confirmScheduleChange');
      case 'deleteContent':
        return t('dashboard.training.trainingwebsites.confirmDeleteContent');
      case 'showError': // New case for displaying errors
        return confirmData; // confirmData will hold the error message string
      default:
        return '';
    }
  };

  // Handle confirmation/modal close
  const handleConfirm = async () => {
    setShowConfirmModal(false);

    switch (confirmAction) {
      case 'syncWebsite':
        await handleSyncWebsiteHome(confirmData);
        break;
      case 'syncLinks':
        await handleSyncWebsiteLinks(confirmData);
        break;
      case 'syncLink':
        await handleSyncLink(confirmData);
        break;
      case 'scheduleChange':
        await handleScheduleChange(confirmData.websiteItem, confirmData.scheduleType);
        break;
      case 'deleteContent':
        await handleConfirmDeleteContent();
        break;
      case 'showError': // Error modal just closes
        // No action needed, modal is already closing
        break;
      default:
        break;
    }
  };

  // Handle actual content deletion after confirmation
  const handleConfirmDeleteContent = async () => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        content_names: [contentData.title],
      });

      const response = await fetch(`${apiUrl}/api/v1/train/delete-contents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        throw new Error(t('dashboard.training.trainingwebsites.errorDeleteContentFailed'));
      }

      // Close the modal and refresh the data
      setShowContentModal(false);
      await onPollState();
      await onFetchTrainedData();
    } catch (err) {
      alert(err.message);
    }
  };

  // --- Function to sync a single link (for individual sync buttons) ---
  const handleSyncLink = async (linkValue) => {
    try {
      setLoadingStates(prev => ({ ...prev, [linkValue]: true }));

      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        links: [linkValue],
      });
      const response = await fetch(`${apiUrl}/api/v1/train/add-links`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });
      if (!response.ok)
        throw new Error(
          t('dashboard.training.trainingwebsites.errorSyncLinkFailed')
        );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await onPollState();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, [linkValue]: false }));
    }
  };

  // --- Function to sync the website's home URL ---
  const handleSyncWebsiteHome = async (websiteItem) => {
    try {
      // Set loading state for this website
      setLoadingStates(prev => ({ ...prev, [`website_${websiteItem.key}`]: true }));

      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Fetch the first link from this domain to get the actual full URL
      const linksResponse = await fetch(`${apiUrl}/api/v1/train/get-website-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${brandvoxToken}`,
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          domain_name: websiteItem.key,
          page: 1,
          page_size: 1, // Just need the first link
        }),
      });

      if (!linksResponse.ok) {
        throw new Error(t('dashboard.training.trainingwebsites.errorFetchLinksFailed'));
      }

      const linksData = await linksResponse.json();

      if (!linksData.links || linksData.links.length === 0) {
        showConfirm(
          'showError',
          t('dashboard.training.trainingwebsites.errorNoLinksAvailable')
        );
        return;
      }

      // Extract full domain from the first link
      const firstLinkUrl = linksData.links[0].key;
      const url = new URL(firstLinkUrl.startsWith('http') ? firstLinkUrl : `https://${firstLinkUrl}`);
      const homeUrl = url.origin; // This gives us https://brandvoxai.com

      const body = JSON.stringify({
        workspace_id: workspaceId,
        website: homeUrl,
      });
      const response = await fetch(`${apiUrl}/api/v1/train/add-website`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        let errorMessage = t('dashboard.training.trainingwebsites.errorSyncWebsiteFailed');
        let showErrorModal = false;
        try {
          const errorData = await response.json();
          // Check for the specific "already scanned" error
          if (response.status === 400 && errorData.message === 'Website already scanned') {
            errorMessage = t('dashboard.training.trainingwebsites.errorWebsiteAlreadyScanned');
            showErrorModal = true; // Flag to show error in modal
          } else if (errorData.message) {
            errorMessage = errorData.message; // Use message from API if available
          }
        } catch (jsonError) {
        }

        if (showErrorModal) {
          showConfirm('showError', errorMessage); // Show specific error in modal
        } else {
          alert(errorMessage); // Show other errors as alert
        }
        return; // Stop execution after handling the error
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      await onPollState();
    } catch (err) { // Catch any unexpected errors during the process (e.g., network issues)
      alert(t('dashboard.training.trainingwebsites.errorUnexpected')); // Generic fallback alert
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [`website_${websiteItem.key}`]: false }));
    }
  };

  // --- Function to sync website links ---
  const handleSyncWebsiteLinks = async (websiteItem) => {
    try {
      // Set loading state for this website's links
      setLoadingStates(prev => ({ ...prev, [`links_${websiteItem.key}`]: true }));

      // Use the domain name directly to construct the domain URL
      // websiteItem.key is already the clean domain name (e.g., "example")
      const domain = `https://${websiteItem.key}`;

      // Validate the constructed URL
      try {
        new URL(domain); // Just to validate format
      } catch (err) {
        alert(
          t('dashboard.training.trainingwebsites.errorInvalidDomain')
        );
        return;
      }
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        website: domain,
      });
      const response = await fetch(`${apiUrl}/api/v1/train/add-website-links`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });
      if (!response.ok)
        throw new Error(
          t('dashboard.training.trainingwebsites.errorSyncLinksFailed')
        );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await onPollState();
    } catch (err) {
      alert(err.message);
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [`links_${websiteItem.key}`]: false }));
    }
  };

  // --- Function to change the website's schedule ---
  const handleScheduleChange = async (websiteItem, scheduleType) => {
    // Check if this schedule type is allowed for the user's plan
    if (!isScheduleAllowed(scheduleType)) {
      // Show error message for plan restriction
      showConfirm('showError', t('dashboard.training.trainingwebsites.errorScheduleNotAvailable'));
      return;
    }

    try {
      // Set loading state for schedule update
      setLoadingStates(prev => ({ ...prev, [`schedule_${websiteItem.key}`]: true }));

      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        website: websiteItem.key,
        schedule_type: scheduleType,
      });
      const response = await fetch(
        `${apiUrl}/api/v1/train/schedule-website-scan-change`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json',
          },
          body,
        }
      );
      if (!response.ok)
        throw new Error(
          t('dashboard.training.trainingwebsites.errorUpdateScheduleFailed')
        );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await onPollState();
      await onFetchTrainedData();
    } catch (err) {
      alert(err.message);
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [`schedule_${websiteItem.key}`]: false }));
    }
  };

  // NEW: Function to fetch links for a specific website (domain)
  const fetchWebsiteLinks = async (domainName, page = 1, pageSize = 10, searchQuery = null) => {
    try {
      setLoadingLinks(prev => ({ ...prev, [domainName]: true }));
      const brandvoxToken = localStorage.getItem('brandvox_token');

      const requestBody = {
        workspace_id: workspaceId,
        domain_name: domainName,
        page: page,
        page_size: pageSize
      };

      // Add search_query only if it's provided and not empty
      if (searchQuery && searchQuery.trim()) {
        requestBody.search_query = searchQuery.trim();
      }

      const response = await fetch(
        `${apiUrl}/api/v1/train/get-website-links`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(t('dashboard.training.trainingwebsites.errorFetchLinksFailed'));
      }

      const data = await response.json();

      // Update website links state
      setWebsiteLinks(prev => ({
        ...prev,
        [domainName]: {
          links: data.links,
          totalCount: data.total_count,
          totalPages: data.total_pages,
          currentPage: data.current_page
        }
      }));

    } catch (err) {
      alert(err.message);
    } finally {
      setLoadingLinks(prev => ({ ...prev, [domainName]: false }));
    }
  };

  // Handler for website link pagination
  const handleWebsiteLinkPageChange = (domainName, page) => {
    const searchQuery = websiteLinkSearch[domainName] || '';
    fetchWebsiteLinks(domainName, page, LINKS_PAGE_SIZE, searchQuery);
  };

  // Toggle website expansion - useEffect will handle fetching
  const handleToggleWebsite = (key) => {
    onWebsiteToggle((prevKey) => (prevKey === key ? null : key));
  };

  const renderPagination = (totalPages, currentPageValue, onPageChangeFunc) => {
    if (totalPages <= 1) return null;

    // Mobile-first responsive pagination
    const getVisiblePages = () => {
      const isMobile = windowWidth <= 576;
      const isTablet = windowWidth <= 768;

      let maxVisible;
      if (isMobile) {
        maxVisible = 3; // Show only 3 pages on mobile
      } else if (isTablet) {
        maxVisible = 5; // Show 5 pages on tablet
      } else {
        maxVisible = 7; // Show 7 pages on desktop
      }

      let startPage = 1;
      let endPage = totalPages;

      if (totalPages > maxVisible) {
        const half = Math.floor(maxVisible / 2);

        if (currentPageValue <= half + 1) {
          // Near the beginning
          endPage = maxVisible;
        } else if (currentPageValue >= totalPages - half) {
          // Near the end
          startPage = totalPages - maxVisible + 1;
        } else {
          // In the middle
          startPage = currentPageValue - half;
          endPage = currentPageValue + half;
        }
      }

      return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    };

    const visiblePages = getVisiblePages();

    // Show first/last on all devices with ellipsis when there are gaps
    const showFirstPage = visiblePages[0] > 1;
    const showLastPage = visiblePages[visiblePages.length - 1] < totalPages;

    return (
      <div className="pagination-wrapper">
        <Pagination className="custom-pagination justify-content-center">
          <Pagination.Prev
            disabled={currentPageValue === 1}
            onClick={() => onPageChangeFunc(currentPageValue - 1)}
          />

          {/* Show first page if not in visible range */}
          {showFirstPage && (
            <>
              <Pagination.Item
                onClick={() => onPageChangeFunc(1)}
                active={currentPageValue === 1}
              >
                1
              </Pagination.Item>
              {/* Show ellipsis when there's a gap between first page and visible pages */}
              {visiblePages[0] > 2 && <Pagination.Ellipsis disabled />}
            </>
          )}

          {/* Show visible page numbers */}
          {visiblePages.map((number) => (
            <Pagination.Item
              key={number}
              active={number === currentPageValue}
              onClick={() => onPageChangeFunc(number)}
            >
              {number}
            </Pagination.Item>
          ))}

          {/* Show last page if not in visible range */}
          {showLastPage && (
            <>
              {/* Show ellipsis when there's a gap between visible pages and last page */}
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && <Pagination.Ellipsis disabled />}
              <Pagination.Item
                onClick={() => onPageChangeFunc(totalPages)}
                active={currentPageValue === totalPages}
              >
                {totalPages}
              </Pagination.Item>
            </>
          )}

          <Pagination.Next
            disabled={currentPageValue === totalPages}
            onClick={() => onPageChangeFunc(currentPageValue + 1)}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="training-websites-container">
      {/* Show URL limit warning when reached */}
      {isUrlLimitReached && (
        <CompactUpgradePrompt
          message={t('dashboard.training.trainingwebsites.upgradeMessage')}
          buttonText={t('dashboard.training.trainingwebsites.upgrade')}
          type="quota"
          percentage={100}
        />
      )}

      {editMode && data.length > 0 && (
        <div className="mb-3">
          <div className="mb-2">
            {isAllWebsitesSelected ? (
              <OutlinedPrimaryButton
                className="mobile-full-width"
                onClick={(e) => {
                  e.stopPropagation();
                  onWebsiteSelect(); // Call without a specific website to select all
                }}
                disabled={systemStatus !== 'OK' || selectAllLoading['__all__']}
              >
                {selectAllLoading['__all__'] ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('dashboard.training.trainingwebsites.loadingText')}
                  </>
                ) : (
                  t('dashboard.training.trainingwebsites.deselectAllWebsites')
                )}
              </OutlinedPrimaryButton>
            ) : (
              <OutlinedPrimaryButton
                className="mobile-full-width"
                onClick={(e) => {
                  e.stopPropagation();
                  onWebsiteSelect(); // Call without a specific website to select all
                }}
                disabled={systemStatus !== 'OK' || selectAllLoading['__all__']}
              >
                {selectAllLoading['__all__'] ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('dashboard.training.trainingwebsites.loadingText')}
                  </>
                ) : (
                  t('dashboard.training.trainingwebsites.selectAllWebsites')
                )}
              </OutlinedPrimaryButton>
            )}
          </div>
        </div>
      )}

      {itemsToShow.map((website) => {
        const isExpanded = expandedWebsiteKey === website.key;
        const websiteKey = website.key;

        // NEW: Get links from fetched state instead of embedded in website data
        const fetchedLinkData = websiteLinks[websiteKey];
        const links = fetchedLinkData?.links || [];
        const totalLinkPages = fetchedLinkData?.totalPages || 1;
        const currentWebsitePage = fetchedLinkData?.currentPage || 1;
        const isLoadingLinks = loadingLinks[websiteKey];

        // Use fetched links directly (they're already paginated and filtered from API)
        const linksToShow = links;

        // Logic to determine if all links of this website are selected
        // Count how many selected items belong to this website (by checking if they start with the domain)
        const selectedLinksForThisWebsite = selectedItems.filter(key =>
          key.startsWith(website.key) || key.includes(website.key)
        ).length;

        // All links are selected only if selected count equals total link count
        const allLinksSelected =
          website.link_count > 0 &&
          selectedLinksForThisWebsite >= website.link_count;

        // Loading state flags
        const isSyncWebsiteLoading = loadingStates[`website_${websiteKey}`];
        const isSyncLinksLoading = loadingStates[`links_${websiteKey}`];
        const isScheduleLoading = loadingStates[`schedule_${websiteKey}`];

        return (
          <Card
            key={website.key}
            className={`training-card ${editMode && allLinksSelected ? 'website-all-selected' : ''}`}
            style={{ cursor: 'default' }}
          >
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => handleToggleWebsite(website.key)}
            >
              <div
                className="d-flex align-items-center flex-grow-1 cursor-pointer"
              >
                {isExpanded ? (
                  <BsChevronDown className="me-2" />
                ) : (
                  <BsChevronRight className="me-2" />
                )}
                <span className="fw-bold website-title text-truncate">{website.key}</span>
                {editMode && allLinksSelected && (
                  <span className="ms-2 badge bg-primary">{t('dashboard.training.trainingwebsites.allSelected')}</span>
                )}
              </div>
              <div className="d-flex website-item-actions" onClick={(e) => e.stopPropagation()}>
                {editMode && (
                  allLinksSelected ? (
                    <OutlinedPrimaryButton
                      className="me-2 select-all-btn d-flex align-items-center"
                      disabled={systemStatus !== 'OK' || selectAllLoading[website.key]}
                      onClick={(e) => {
                        e.stopPropagation();
                        onWebsiteSelect(website);
                      }}
                    >
                      {selectAllLoading[website.key] ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          {t('dashboard.training.trainingwebsites.loadingText')}
                        </>
                      ) : (
                        <>
                          <BsCheck2Square className="me-1" />
                          {t('dashboard.training.trainingwebsites.deselectAll')}
                        </>
                      )}
                    </OutlinedPrimaryButton>
                  ) : (
                    <OutlinedPrimaryButton
                      className="me-2 select-all-btn d-flex align-items-center"
                      disabled={systemStatus !== 'OK' || selectAllLoading[website.key]}
                      onClick={(e) => {
                        e.stopPropagation();
                        onWebsiteSelect(website);
                      }}
                    >
                      {selectAllLoading[website.key] ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          {t('dashboard.training.trainingwebsites.loadingText')}
                        </>
                      ) : (
                        <>
                          <BsSquare className="me-1" />
                          {t('dashboard.training.trainingwebsites.selectAll')}
                        </>
                      )}
                    </OutlinedPrimaryButton>
                  )
                )}

                {/* Sync Website button - Only show when not in edit mode and user is admin */}
                {!editMode && isAdmin && wrapWithTooltip(
                  <PrimaryButton
                    className="me-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      showConfirm('syncWebsite', website);
                    }}
                    disabled={systemStatus !== 'OK' || isSyncWebsiteLoading || isUrlLimitReached}
                  >
                    {isSyncWebsiteLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <div className="d-flex align-items-center">
                        <BsGlobe className="sync-web-icon" />
                        <span className="d-none d-md-inline"> {t('dashboard.training.trainingwebsites.syncWebsite')}</span>
                      </div>
                    )}
                  </PrimaryButton>,
                  t('dashboard.training.trainingwebsites.tooltipSyncWebsite')
                )}

                {/* Sync Links button - Only show when not in edit mode and user is admin */}
                {!editMode && isAdmin && wrapWithTooltip(
                  <PrimaryButton
                    onClick={(e) => {
                      e.stopPropagation();
                      showConfirm('syncLinks', website);
                    }}
                    disabled={systemStatus !== 'OK' || isSyncLinksLoading || isUrlLimitReached}
                  >
                    {isSyncLinksLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <div className="d-flex align-items-center">
                        <BsLink className="sync-web-icon" />
                        <span className="d-none d-md-inline"> {t('dashboard.training.trainingwebsites.syncLinks')}</span>
                      </div>
                    )}
                  </PrimaryButton>,
                  t('dashboard.training.trainingwebsites.tooltipSyncLinks')
                )}
              </div>
            </Card.Header>

            {isExpanded && (
              <Card.Body onClick={(e) => e.stopPropagation()}>
                {/* Schedule Controls Section - Only visible when expanded, not in edit mode, and user is admin */}
                {!editMode && isAdmin && (
                  <div className="schedule-controls-section mb-4">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                      <div className="schedule-info mb-2 mb-md-0">
                        <h6 className="mb-1"> {t('dashboard.training.trainingwebsites.syncSchedule')}</h6>
                        <div className="current-schedule d-flex align-items-center">
                          <BsClockHistory className="me-2 text-muted" />
                          <span className="fw-medium"> {t('dashboard.training.trainingwebsites.currentSchedule')}</span>
                          <span className="schedule-value ms-1">
                            {website.schedule_train ? website.schedule_train[0] : 'None'}
                          </span>
                        </div>

                        {/* Last Scan Date Display */}
                        <div className="last-scan-info d-flex align-items-center mt-2">
                          <BsCalendarEvent className="me-2 text-muted" />
                          <span className="fw-medium"> {t('dashboard.training.trainingwebsites.lastScan')}:</span>
                          <span className="last-scan-value ms-1">
                            {website.schedule_train && website.schedule_train[1]
                              ? new Date(website.schedule_train[1] + 'Z').toLocaleString()
                              : t('dashboard.training.trainingwebsites.neverScanned')}
                          </span>
                        </div>
                      </div>

                      {/* Schedule Dropdown - Inside card body */}
                      {wrapWithTooltip(
                        <div className="schedule-dropdown-container">
                          <DropdownButton
                            title={
                              isScheduleLoading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                              ) : (
                                <>
                                  <BsCalendarEvent className="me-2" />
                                  <span style={{ fontSize: '14px' }}> {t('dashboard.training.trainingwebsites.changeSchedule')}</span>
                                </>
                              )
                            }
                            className="schedule-dropdown modern-dropdown mobile-full-width"
                            disabled={systemStatus !== 'OK' || isScheduleLoading}
                          >
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                showConfirm('scheduleChange', { websiteItem: website, scheduleType: 'daily' });
                              }}
                              active={website.schedule_train && website.schedule_train[0] === 'Daily'}
                              className={`schedule-option ${!isScheduleAllowed('daily') ? 'disabled' : ''}`}
                              disabled={!isScheduleAllowed('daily')}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center">
                                  <BsClockHistory className="me-2" />
                                  {t('dashboard.training.trainingwebsites.daily')}
                                  {!isScheduleAllowed('daily') && (
                                    <span className="ms-2 text-danger small">
                                      (Upgrade)
                                    </span>
                                  )}
                                </div>
                                {website.schedule_train && website.schedule_train[0] === 'Daily' && (
                                  <BsCheckCircle className="text-success" />
                                )}
                              </div>
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                showConfirm('scheduleChange', { websiteItem: website, scheduleType: 'weekly' });
                              }}
                              active={website.schedule_train && website.schedule_train[0] === 'Weekly'}
                              className={`schedule-option ${!isScheduleAllowed('weekly') ? 'disabled' : ''}`}
                              disabled={!isScheduleAllowed('weekly')}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center">
                                  <BsClockHistory className="me-2" />
                                  {t('dashboard.training.trainingwebsites.weekly')}
                                  {!isScheduleAllowed('weekly') && (
                                    <span className="ms-2 text-danger small">
                                      (Upgrade)
                                    </span>
                                  )}
                                </div>
                                {website.schedule_train && website.schedule_train[0] === 'Weekly' && (
                                  <BsCheckCircle className="text-success" />
                                )}
                              </div>
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                showConfirm('scheduleChange', { websiteItem: website, scheduleType: 'monthly' });
                              }}
                              active={website.schedule_train && website.schedule_train[0] === 'Monthly'}
                              className={`schedule-option ${!isScheduleAllowed('monthly') ? 'disabled' : ''}`}
                              disabled={!isScheduleAllowed('monthly')}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center">
                                  <BsClockHistory className="me-2" />
                                  {t('dashboard.training.trainingwebsites.monthly')}
                                  {!isScheduleAllowed('monthly') && (
                                    <span className="ms-2 text-danger small">
                                      (Upgrade)
                                    </span>
                                  )}
                                </div>
                                {website.schedule_train && website.schedule_train[0] === 'Monthly' && (
                                  <BsCheckCircle className="text-success" />
                                )}
                              </div>
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                showConfirm('scheduleChange', { websiteItem: website, scheduleType: 'none' });
                              }}
                              active={!website.schedule_train || website.schedule_train[0] === 'None'}
                              className="schedule-option"
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center">
                                  <BsClockHistory className="me-2" />
                                  {t('dashboard.training.trainingwebsites.none')}
                                </div>
                                {(!website.schedule_train || website.schedule_train[0] === 'None') && (
                                  <BsCheckCircle className="text-success" />
                                )}
                              </div>
                            </Dropdown.Item>
                          </DropdownButton>
                        </div>,
                        t('dashboard.training.trainingwebsites.tooltipChangeSchedule')
                      )}
                    </div>
                  </div>
                )}

                {/* Search input */}
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder={t('dashboard.training.trainingwebsites.searchLinks')}
                    value={websiteLinkSearch[websiteKey] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      onWebsiteLinkSearch((prev) => ({
                        ...prev,
                        [websiteKey]: value,
                      }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="search-input"
                  />
                </Form.Group>

                {/* Show loading indicator while fetching links */}
                {isLoadingLinks ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden"> {t('dashboard.training.trainingwebsites.loadingLinks')}</span>
                    </Spinner>
                    <p className="mt-2 text-muted"> {t('dashboard.training.trainingwebsites.loadingLinks')}</p>
                  </div>
                ) : linksToShow && linksToShow.length > 0 ? (
                  linksToShow.map((link) => {
                    const isSelected = selectedItems.includes(link.key);
                    const linkLoading = loadingStates[link.key];

                    return (
                      <Card
                        key={link.key}
                        className={`mb-2 training-card ${editMode ? (isSelected ? 'selected' : 'unselected') : ''
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editMode) {
                            onItemSelect(link.key);
                          } else {
                            handleViewContent(link.key);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="d-flex justify-content-between align-items-center py-2">
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center">
                              {editMode && (
                                <div className="select-indicator me-2">
                                  {isSelected ?
                                    <BsCheck2Square className="text-primary-custom" size={18} /> :
                                    <BsSquare className="text-muted" size={18} />}
                                </div>
                              )}
                              <BsLink className="me-2 flex-shrink-0" />
                              <div className="link-container">
                                <span className="link-url" style={{
                                  direction: 'rtl',
                                  textAlign: 'left'
                                }}>
                                  {link.key}
                                </span>

                                {/* Sync Link button - Only show when not in edit mode and user is admin */}
                                {!editMode && isAdmin && wrapWithTooltip(
                                  <div className="sync-wrapper">
                                    <PrimaryButton
                                      className="mobile-card-action flex-shrink-0"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        showConfirm('syncLink', link.key);
                                      }}
                                      disabled={systemStatus !== 'OK' || linkLoading || isUrlLimitReached}
                                      width="32px"
                                      height="32px"
                                      padding="0"
                                    >
                                      {linkLoading ? (
                                        <Spinner animation="border" size="sm" />
                                      ) : (
                                        <BsArrowRepeat className="sync-icon" />
                                      )}
                                    </PrimaryButton>
                                  </div>,
                                  t('dashboard.training.trainingwebsites.tooltipSyncLink')
                                )}
                              </div>
                            </div>
                            <small>
                              {link.size}{' '}
                              {t('dashboard.training.trainingwebsites.characters')}
                            </small>
                            {/* Display assigned chatbots and apps */}
                            {link.assigned_to && (
                              (link.assigned_to.chatbots?.length > 0 || link.assigned_to.apps?.length > 0) && (
                                <div className="mt-2">
                                  <small className="text-muted">
                                    {t('dashboard.training.trainingwebsites.assignedTo')}:
                                  </small>
                                  <div className="d-flex flex-wrap gap-1 mt-1">
                                    {/* Display chatbots */}
                                    {link.assigned_to.chatbots?.map(chatbotId => {
                                      const chatbot = chatbots.find(c => c.chatbot_id === chatbotId);
                                      return chatbot ? (
                                        <span key={`chatbot-${chatbotId}`} className="badge assignment-badge-chatbot">
                                          {t('dashboard.training.trainingwebsites.supportBV')}: {chatbot.chatbot_name}
                                        </span>
                                      ) : null;
                                    })}
                                    {/* Display apps */}
                                    {link.assigned_to.apps?.map(appId => {
                                      const app = apps.find(a => a.app_id === appId);
                                      if (!app) return null;
                                      const appColor = getAppColor(app.name);
                                      const appDisplayName = getAppDisplayName(app.name, t);
                                      return (
                                        <span
                                          key={`app-${appId}`}
                                          className="badge assignment-badge-app"
                                          style={{ color: appColor, borderColor: appColor }}
                                        >
                                          {appDisplayName}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </Card.Body>
                        {editMode && (
                          <div className={`selection-overlay ${isSelected ? 'selected' : ''}`}></div>
                        )}
                      </Card>
                    );
                  })
                ) : (
                  <p>
                    {t('dashboard.training.trainingwebsites.noLinksAvailable')}
                  </p>
                )}
                {/* Show pagination for website links */}
                {totalLinkPages > 1 &&
                  renderPagination(totalLinkPages, currentWebsitePage, (page) =>
                    handleWebsiteLinkPageChange(websiteKey, page)
                  )}
              </Card.Body>
            )}
          </Card>
        );
      })}
      {renderPagination(
        Math.ceil(data.length / PAGE_SIZE),
        currentPage,
        (page) => onPageChange('web', page)
      )}

      {/* Content viewing modal */}
      <Modal
        show={showContentModal}
        onHide={() => setShowContentModal(false)}
        centered
        size="xl"
        className="modern-content-modal"
      >
        <Modal.Header className="modern-modal-header">
          <div className="modal-title-section">
            {isContentLoading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-3" />
                <span className="loading-text"> {t('dashboard.training.trainingwebsites.loadingContent')}</span>
              </div>
            ) : (
              <>
                <h4 className="modal-title-text">{contentData.title}</h4>
                <div className="modal-metadata">
                  {contentData.type && (
                    <span className="metadata-badge">
                      {contentData.type}
                    </span>
                  )}
                  {contentData.created_at && (
                    <span className="metadata-date">
                      {new Date(contentData.created_at + 'Z').toLocaleString()}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={() => setShowContentModal(false)}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="m6 6 12 12M6 18 18 6" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body className="modern-modal-body">
          {isContentLoading ? (
            <div className="loading-container">
              <div className="loading-content">
                <Spinner animation="border" className="loading-spinner" />
                <p className="loading-message"> {t('dashboard.training.trainingwebsites.loadingContent')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="content-wrapper">
                <style>{`
                  .content-display {
                    font-size: 0.875rem;
                    max-width: 100%;
                    line-height: 1.7;
                  }
                  .content-display p {
                    margin-bottom: 0.5rem;
                    line-height: 1.625;
                  }
                  .content-display p:last-child {
                    margin-bottom: 0;
                  }
                  .content-display br {
                    display: block;
                    margin-bottom: 0.375rem;
                  }
                  .content-display h1 {
                    font-size: 1.125rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    margin-top: 0.5rem;
                  }
                  .content-display h1:first-child {
                    margin-top: 0;
                  }
                  .content-display h2 {
                    font-size: 1rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    margin-top: 0.5rem;
                  }
                  .content-display h2:first-child {
                    margin-top: 0;
                  }
                  .content-display h3 {
                    font-size: 0.875rem;
                    font-weight: 700;
                    margin-bottom: 0.375rem;
                    margin-top: 0.5rem;
                  }
                  .content-display h3:first-child {
                    margin-top: 0;
                  }
                  .content-display ul {
                    list-style-type: disc;
                    list-style-position: inside;
                    margin-bottom: 0.75rem;
                    padding-left: 0;
                  }
                  .content-display ol {
                    list-style-type: decimal;
                    list-style-position: inside;
                    margin-bottom: 1rem;
                    padding-left: 0;
                  }
                  .content-display li {
                    margin-bottom: 0.25rem;
                  }
                  .content-display blockquote {
                    border-left: 4px solid rgba(107, 114, 128, 0.3);
                    padding-left: 1rem;
                    font-style: italic;
                    margin: 1rem 0;
                  }
                  .content-display code {
                    background-color: rgba(0, 0, 0, 0.05);
                    padding: 0.125rem 0.375rem;
                    border-radius: 0.25rem;
                    font-size: 0.875rem;
                    font-family: 'Courier New', Courier, monospace;
                    display: inline;
                    margin: 0;
                    line-height: inherit;
                    vertical-align: baseline;
                  }
                  .content-display pre {
                    margin: 1rem 0;
                  }
                  .content-display pre code {
                    display: block;
                    background-color: rgba(0, 0, 0, 0.05);
                    padding: 1rem;
                    border-radius: 0.5rem;
                    overflow-x: auto;
                    font-size: 0.875rem;
                    font-family: 'Courier New', Courier, monospace;
                  }
                  .content-display a {
                    color: black;
                    text-decoration: none;
                  }
                  .content-display a:hover {
                    text-decoration: underline;
                  }
                  .content-display strong {
                    font-weight: 700;
                  }
                  .content-display em {
                    font-style: italic;
                  }
                  .content-display table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1rem 0;
                    overflow-x: auto;
                    display: block;
                  }
                  .content-display thead {
                    background-color: rgba(0, 0, 0, 0.05);
                  }
                  .content-display tbody {
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                  }
                  .content-display tr {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                  }
                  .content-display th {
                    padding: 0.5rem 1rem;
                    text-align: left;
                    font-size: 0.875rem;
                    font-weight: 600;
                  }
                  .content-display td {
                    padding: 0.5rem 1rem;
                    font-size: 0.875rem;
                  }
                  .content-display img {
                    max-width: 50%;
                    max-height: 400px;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    border-radius: 0.5rem;
                    margin: 1rem 0;
                    display: block;
                  }
                  .content-display hr {
                    border: none;
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    margin: 1rem 0;
                  }
                `}</style>
                <div className="content-display" dangerouslySetInnerHTML={{
                  __html: marked.parse(contentPages[contentPage - 1] || '')
                }} />
              </div>

              {/* Modern pagination for long content */}
              {contentPages.length > 1 && (
                <div className="modern-pagination-container">
                  <div className="pagination-info">
                    <span className="page-indicator">
                      {contentPage} of {contentPages.length}
                    </span>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-control"
                      onClick={() => setContentPage(prev => Math.max(prev - 1, 1))}
                      disabled={contentPage === 1}
                      aria-label="Previous page"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </button>

                    <div className="page-numbers">
                      {(() => {
                        const totalPages = contentPages.length;
                        const isMobile = windowWidth < 576;
                        const maxVisible = isMobile ? 3 : 5;

                        let startPage = Math.max(1, contentPage - Math.floor(maxVisible / 2));
                        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                        if (endPage - startPage + 1 < maxVisible) {
                          startPage = Math.max(1, endPage - maxVisible + 1);
                        }

                        return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                          const pageNum = startPage + i;
                          return (
                            <button
                              key={pageNum}
                              className={`page-number ${contentPage === pageNum ? 'active' : ''}`}
                              onClick={() => setContentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      className="pagination-control"
                      onClick={() => setContentPage(prev => Math.min(prev + 1, contentPages.length))}
                      disabled={contentPage === contentPages.length}
                      aria-label="Next page"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Confirmation/Error modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className={`fw-bold ${confirmAction === 'deleteContent' || confirmAction === 'showError' ? 'text-danger' : 'text-primary-custom'}`}>
            {confirmAction === 'deleteContent'
              ? (t('dashboard.training.trainingwebsites.confirmDeletion'))
              : confirmAction === 'showError'
                ? ('Error') // Title for error modal
                : (t('dashboard.training.trainingwebsites.confirmAction'))}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="d-flex">
            <div className={`me-3 ${confirmAction === 'deleteContent' || confirmAction === 'showError' ? 'text-danger' : 'text-primary-custom'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                {confirmAction === 'deleteContent' || confirmAction === 'showError'
                  ? <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" /> // Exclamation for error/delete
                  : <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" /> // Question mark for confirm
                }
              </svg>
            </div>
            <p className="mb-0">{getConfirmMessage()}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          {confirmAction !== 'showError' && (
            <CancelButton
              onClick={() => setShowConfirmModal(false)}
              className="me-2"
            >
              {t('dashboard.training.trainingwebsites.cancelButton')}
            </CancelButton>
          )}
          {confirmAction === 'deleteContent' ? (
            <DangerButton
              onClick={handleConfirm}
            >
              <span className="d-flex align-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
                </svg>
                {t('dashboard.training.trainingwebsites.deleteButton')}
              </span>
            </DangerButton>
          ) : confirmAction === 'showError' ? (
            <PrimaryButton
              onClick={handleConfirm}
            >
              {t('dashboard.training.trainingwebsites.okButton')}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              onClick={handleConfirm}
            >
              <span className="d-flex align-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                  <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z" />
                </svg>
                {t('dashboard.training.trainingwebsites.confirmButton')}
              </span>
            </PrimaryButton>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TrainingWebsites;
